import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { FormProvider, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { QueryObserverResult, RefetchOptions, useMutation, useQuery } from '@tanstack/react-query';
import uniqueString from 'unique-string';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(isSameOrBefore);

import API from 'api';
import { BrandDto } from 'api/repositories/brand/brand.types';
import { queries } from 'api/api.constants';
import { GET_PRODUCTS_DTO_ENUMS, PRODUCT_DTO_ENUMS } from 'api/repositories/product/product.constants';
import { ProductsDto } from 'api/repositories/product/product.types';
import { CampaignDto, StoreOrUpdateTemporaryCampaignRequestParams } from 'api/repositories/campaign/campaign.types';
import { Settings } from 'types/settings';
import { mapDispatchProps, mapStateToProps } from 'store/store';
import { CAMPAIGN_DTO_ENUMS, getCampaignCommissionOptions } from 'api/repositories/campaign/campaign.constants';
import useGetBrand from 'hooks/useGetBrand/useGetBrand';
import useGetSettings from 'hooks/useGetSettings/useGetSettings';
import {
  CampaignFormProps,
  CampaignPaymentFormProps,
  GetParsedCampaignParams,
  GetParsedCampaignResponse,
  GuidelineRuleFormField,
  GuidelineRuleFormProps,
  OnSuccessStoreOrUpdateTemporaryCampaignCallbackResponse,
} from './campaignForm.types';
import {
  BEAUTY_OUTPUT_GROUP_NAMES,
  BEAUTY_ATTACHMENT_GROUP_NAMES,
  FASHION_VIDEO_OUTPUT_GROUP_NAMES,
  GUIDELINE_RULE_NOT_ALLOWED_UNDEFINED_VALUE_GROUP_NAMES,
  GUIDELINE_RULE_ARRAY_GROUPS,
  GUIDELINE_RULE_TAG_GROUPS,
  MAX_LENGTH,
} from './campaignForm.constants';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { GetGuidelineRulesRequestParams } from 'api/repositories/guidelineRules/guidelineRules.types';
import { CAMPAIGN_FORM_STEP_ONE_FIELD_NAME } from './components/campaignFormContainer/components/campaignFormSteps/components/campaignFormStepOne/campaignFormStepOne.constants';
import { useLanguage } from 'assets/languages';
import useDebugForm from 'hooks/useDebugForm';

interface StoreOrUpdateTemporaryCampaignMutateParams
  extends Pick<StoreOrUpdateTemporaryCampaignRequestParams, 'campaign' | 'guidelineRules' | 'campaignManagers'> {
  options?: {
    onSuccess?: (response: OnSuccessStoreOrUpdateTemporaryCampaignCallbackResponse) => void;
  };
}

interface InitializeFormDataProps {
  loadedCampaign?: CampaignDto;
}

export interface CampaignFormContextProps {
  modal: {
    showModal: (modalProps: any) => void;
    showAlertModal: (modalProps: any) => void;
    closeModal: () => void;
    showOverlays: (modalProps: any) => void;
    closeOverlays: () => void;
  };
  auth: { token: string; isMaster: boolean } | null;
  brand: BrandDto | null;
  refetchBrand: (options?: RefetchOptions) => Promise<QueryObserverResult<BrandDto, Error>>;
  setLegacyBrand: (brand: any) => void;
  settings: Settings | null;
  products: ProductsDto['products'];
  refetchProducts: () => void;
  campaignFormMethods: UseFormReturn<CampaignFormProps> | null;
  guidelineRuleFormMethods: UseFormReturn<GuidelineRuleFormProps> | null;
  campaignPaymentFormMethods: UseFormReturn<CampaignPaymentFormProps> | null;
  temporaryKey: string | null;
  setTemporaryKey: Dispatch<SetStateAction<string | null>>;
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  checkedInspectionAgreement: boolean;
  setCheckedInspectionAgreement: Dispatch<SetStateAction<boolean>>;
  showLoader: boolean;
  setShowLoader: Dispatch<SetStateAction<boolean>>;
  isInitializedGuidelineRules: boolean;
  setIsInitializedGuidelineRules: Dispatch<SetStateAction<boolean>>;
  updateCampaignId: number | null;
  getGuidelineRulesParams: GetGuidelineRulesRequestParams | null;
  prepareStoreCampaignFormData: (campaign: Partial<CampaignDto>) => Partial<CampaignDto>;
  storeOrUpdateTemporaryCampaignMutate: (params: StoreOrUpdateTemporaryCampaignMutateParams) => void;
  initializeCampaignFormData: (props: InitializeFormDataProps) => void;
  getParsedCampaign: (params: GetParsedCampaignParams) => GetParsedCampaignResponse;
  getParsedGuidelineRules: () => StoreOrUpdateTemporaryCampaignRequestParams['guidelineRules'];
  getInitialCommissionData: (
    brand: Pick<BrandDto, 'currency' | 'destinationCountry'>,
    loadedCampaign: undefined | Pick<CampaignDto, 'commissionAmount' | 'commissionIncreaseMaxStep'>,
  ) => {
    commissionAmount: number;
    commissionPrice: number;
    commissionIncreaseMaxStep: number;
  };
}

const initCampaignFormContextData: CampaignFormContextProps = {
  modal: {
    showModal: () => {},
    showAlertModal: () => {},
    closeModal: () => {},
    showOverlays: () => {},
    closeOverlays: () => {},
  },
  auth: null,
  brand: null,
  refetchBrand: async () => ({ data: null } as unknown as QueryObserverResult<BrandDto, Error>),
  setLegacyBrand: () => {},
  settings: null,
  products: [],
  refetchProducts: () => {},
  campaignFormMethods: null,
  guidelineRuleFormMethods: null,
  campaignPaymentFormMethods: null,
  temporaryKey: uniqueString(),
  setTemporaryKey: () => {},
  step: 1,
  setStep: () => {},
  checkedInspectionAgreement: false,
  setCheckedInspectionAgreement: () => {},
  showLoader: true,
  setShowLoader: () => {},
  isInitializedGuidelineRules: false,
  setIsInitializedGuidelineRules: () => {},
  updateCampaignId: null,
  getGuidelineRulesParams: null,
  prepareStoreCampaignFormData: (campaign: Partial<CampaignDto>) => campaign,
  storeOrUpdateTemporaryCampaignMutate: () => {},
  initializeCampaignFormData: () => {},
  getParsedCampaign: () => ({}),
  getParsedGuidelineRules: () => [],
  getInitialCommissionData: () => ({
    commissionAmount: undefined,
    commissionPrice: undefined,
    commissionIncreaseMaxStep: undefined,
  }),
};

export const CampaignFormContext = createContext<CampaignFormContextProps>(initCampaignFormContextData);

export const CampaignFormContextProvider = connect(
  mapStateToProps,
  mapDispatchProps,
)(
  ({
    children,
    auth,
    showModal,
    closeModal,
    showOverlays,
    closeOverlays,
    campaign: legacyCampaign,
    setCampaign: setLegacyCampaign,
    setBrand: setLegacyBrand,
  }: Pick<CampaignFormContextProps, 'auth'> &
    Omit<CampaignFormContextProps['modal'], 'showAlertModal'> & {
      children: ReactNode;
      campaign: any;
      setCampaign: (campaign: CampaignDto) => void;
      setBrand: (brand: BrandDto) => void;
    }) => {
    const language = useLanguage();
    const params = useParams();

    const campaignFormMethods = useForm<CampaignFormProps>();
    const guidelineRuleFormMethods = useForm<GuidelineRuleFormProps>();
    const campaignPaymentFormMethods = useForm<CampaignPaymentFormProps>();

    const [isInitializedGuidelineRules, setIsInitializedGuidelineRules] = useState<boolean>(false);
    const [isInitializedCampaign, setIsInitializedCampaign] = useState<boolean>(false);
    const [temporaryKey, setTemporaryKey] = useState<string | null>(initCampaignFormContextData.temporaryKey);
    const [step, setStep] = useState<CampaignFormContextProps['step']>(initCampaignFormContextData.step);
    const [checkedInspectionAgreement, setCheckedInspectionAgreement] = useState<
      CampaignFormContextProps['checkedInspectionAgreement']
    >(initCampaignFormContextData.checkedInspectionAgreement);
    const [showLoader, setShowLoader] = useState<CampaignFormContextProps['showLoader']>(
      initCampaignFormContextData.showLoader,
    );

    const outputTypeFl = useWatch({
      control: campaignFormMethods?.control,
      name: 'outputTypeFl',
    });

    const commissionAmount = useWatch({
      control: campaignFormMethods.control,
      name: 'commissionAmount',
    });

    const updateCampaignId: number | null = useMemo(() => {
      const campaignId = Number(params?.campaignId);

      return isNaN(campaignId) ? null : campaignId;
    }, [params?.campaignId]);

    // 브랜드 정보 조회
    const { data: brand, refetch: refetchBrand } = useGetBrand();

    // 서버 설정 가져오기
    const { data: settings } = useGetSettings();

    // 제품 정보 가져오기
    const { data: products, refetch: refetchProducts } = useQuery({
      ...queries.product.getProducts({
        temporaryKey: updateCampaignId ? null : temporaryKey,
        campaignId: updateCampaignId ? updateCampaignId : null,
        type: PRODUCT_DTO_ENUMS.TYPE.PRODUCT,
        brandToken: auth.token,
        orderBy: GET_PRODUCTS_DTO_ENUMS.ORDER_BY.ID_ASC,
        size: MAX_LENGTH,
      }),
      select: (data: any) => data?.data?.products,
      enabled: !!updateCampaignId || isInitializedCampaign,
    });

    const prepareStoreCampaignFormData = (campaign: Partial<CampaignDto>) => {
      if (campaign.isSingleDayPosting && campaign.postingEndsAt) {
        campaign.postingEndsAt = dayjs(campaign.postingEndsAt).add(2, 'day').format('YYYY-MM-DD');
      }
      return campaign;
    }

    // 캠페인 임시 저장
    const { mutate: storeOrUpdateTemporaryCampaignMutate } = useMutation({
      mutationFn: ({ campaign, guidelineRules, campaignManagers }: StoreOrUpdateTemporaryCampaignMutateParams) =>
        API.Campaign.storeOrUpdateTemporaryCampaign({
          brandToken: auth.token,
          step: step,
          temporaryKey,
          campaign: prepareStoreCampaignFormData(campaign),
          guidelineRules,
          campaignManagers,
          userTypeFl: GUIDELINE_RULE_DTO_ENUMS.USER_TYPE_FL.CAMPAIGN_NORMAL,
        }),
      onSuccess: async (response, variables) => {
        await refetchBrand();
        variables?.options?.onSuccess?.(response.data);
      },
      onError: (error: any) => {
        showAlertModal({
          title: language.pages.campaigns.show_alert_modal_temp_save_fail_title,
          text: error.message,
        });
      },
      onSettled: () => {
        setShowLoader(false);
      },
    });

    // 가이드라인 조회 params
    const getGuidelineRulesParams = useMemo(() => {
      return {
        brandToken: auth.token,
        brandCategoryId: brand?.categoryId,
        campaignId: updateCampaignId ? updateCampaignId : null,
        temporaryKey: updateCampaignId ? null : temporaryKey,
        userTypeFl: GUIDELINE_RULE_DTO_ENUMS.USER_TYPE_FL.CAMPAIGN_NORMAL,
        outputType:
          outputTypeFl === CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.PHOTO
            ? GUIDELINE_RULE_DTO_ENUMS.OUTPUT_TYPE.PHOTO
            : GUIDELINE_RULE_DTO_ENUMS.OUTPUT_TYPE.VIDEO,
      };
    }, [auth.token, brand?.categoryId, updateCampaignId, temporaryKey, outputTypeFl]);

    const initializeCampaignFormData = useCallback(
      ({ loadedCampaign }: InitializeFormDataProps) => {
        // 불러온 캠페인 데이터 또는 임시 저장으로 불러온 데이터가 있다면 캠페인 초기화
        if (loadedCampaign) {
          let {
            id,
            createdAt,
            temporaryKey,

            // Step 1
            description,
            isFasttrackSchedule,
            recruitStartsAt,
            recruitEndsAt,
            selectionStartsAt,
            selectionEndsAt,
            productDeployAt,
            inspectionSubmitDueDt,
            inspectionFeedbackDueDt,
            postingStartsAt,
            postingEndsAt,
            isSingleDayPosting,
            deliveryCompanyName,
            // Step 2
            outputTypeFl,
            // Step 3
            brandManagerName,
            brandManagerMobile,
            brandManagerCountryCode,
            returnManagerName,
            returnManagerMobile,
            returnManagerCountryCode,
            returnAddress,
          } = loadedCampaign;

          /**
           * @note 일정 초기화
           */
          const isNotTemporaryCampaign = (temporaryKey == null); // 이미 생성된 캠페인들은 무조건 초기화 (구정책 사용 리스크)
          const validRecruitStartsAt = (
            recruitStartsAt && dayjs().isSameOrBefore(dayjs(recruitStartsAt), 'day')
          ); // 모집 시작일이 지난 경우
          const isOldPolicySingleDayCampaign = (
            isSingleDayPosting && (
              // (temporaryKey != null) ||
              (id != null && createdAt && dayjs(createdAt).isSameOrBefore(dayjs('2025-09-01'), 'day'))
            )
          ); // 25년 8월형 올영세일 특정일 캠페인 (25년 8월 29일 이전 생성)

          const shouldResetDates = (
            isNotTemporaryCampaign ||
            !validRecruitStartsAt
          ); 
          let dates = shouldResetDates
            ? {}
            : {
                recruitStartsAt,
                recruitEndsAt,
                selectionStartsAt,
                selectionEndsAt,
                productDeployAt,
                postingStartsAt,
                postingEndsAt,
              };

          /**
           * @note 특정일 포스팅
           * - postingEndsAt = (특이 케이스) 브랜드와 모델 포스팅일자가 동일하다. 폼에서는 모델 포스팅일자로 입력
           * - 저장 시 모델 포스팅일자 +2일로 저장하고, 불러올 때 모델 포스팅일자(-2일)로 보정
           */
          if (!isOldPolicySingleDayCampaign && brand.isCategoryBeauty && isSingleDayPosting) {
            if (dates.postingEndsAt != null) {
              dates[CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT]
                = dayjs(dates.postingEndsAt).subtract(2, 'day').format('YYYY-MM-DD');
            }
          }

          /**
           * @note 사전검수 체크 및 일정 설정
           */ 
          if (
            brand.isCategoryBeauty && 
            dates.productDeployAt != null && 
            inspectionSubmitDueDt != null
          ) {
            dates[CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_SUBMIT_DUE_DT] = inspectionSubmitDueDt;
            dates[CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.INSPECTION_FEEDBACK_DUE_DT] = inspectionFeedbackDueDt;

            if (inspectionFeedbackDueDt == null) {
              dates[CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_STARTS_AT] = null;
              dates[CAMPAIGN_FORM_STEP_ONE_FIELD_NAME.PERIOD.POSTING_ENDS_AT] = null;
            }
            setCheckedInspectionAgreement(true);
          }

          const {
            commissionAmount: initialCommissionAmount,
            commissionPrice: initialCommissionPrice,
            commissionIncreaseMaxStep: initialCommissionIncreaseMaxStep,
          } = getInitialCommissionData(brand, loadedCampaign);

          const campaignData = {
            id,
            description,
            deliveryCompanyName,
            ...dates,

            // @note isSingleDayPosting|isFasttrackSchedule - 올영 세일기간 UX에 따라 isSingleDayPosting 처리
            isFasttrackSchedule: isOldPolicySingleDayCampaign ? undefined : isFasttrackSchedule,

            isSingleDayPosting: isOldPolicySingleDayCampaign ? undefined : isSingleDayPosting,
            outputTypeFl: outputTypeFl
              ? outputTypeFl
              : brand.isCategoryBeauty
              ? CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.VIDEO
              : CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.PHOTO,
            brandManagerName,
            brandManagerMobile,
            brandManagerCountryCode,
            returnManagerName,
            returnManagerMobile,
            returnManagerCountryCode,
            email: brand.lastCampaign?.email ?? brand.email,
            returnAddress,
            commissionAmount: brand.isCategoryBeauty ? initialCommissionAmount : null,
            commissionIncreaseMaxStep: brand.isCategoryBeauty ? initialCommissionIncreaseMaxStep : null,
          };

          const campaignPaymentData = {
            priceCommission: brand.isCategoryBeauty ? initialCommissionPrice : null,
          };

          /**
           * 결제 페이지에서 store로 관리하고 있는 부분으로 인해 결제 시 store에 저장해야 함
           * TODO: 추후 store 걷어낸 후 아래 로직 삭제
           */
          setLegacyCampaign(loadedCampaign);
          setLegacyBrand(brand);

          campaignFormMethods.reset(campaignData);
          campaignPaymentFormMethods.reset(campaignPaymentData);
          setIsInitializedCampaign(true);

          return campaignData;
        }
      },
      [brand],
    );

    // 서버에 전달할 campaign data parsing
    const getParsedCampaign = useCallback(({ fieldNames }: GetParsedCampaignParams) => {
      const formValues = campaignFormMethods.getValues(fieldNames);

      return fieldNames.reduce((acc, name, index) => {
        acc[name] = formValues[index];
        return acc;
      }, {});
    }, []);

    // 서버에 전달할 guidelineRules data parsing
    const getParsedGuidelineRules = useCallback(() => {
      // group_name이 GUIDE_IMAGE인 경우 order 값 변경 안됨
      const guidelineRuleFormValues = guidelineRuleFormMethods.getValues();
      const currentOutputTypeFl = campaignFormMethods.getValues('outputTypeFl');
      const parsedGuidelineRules = [];

      function isInArray<T extends readonly any[]>(value: string, array: T): value is T[number] {
        return (array as readonly string[]).includes(value as any);
      }

      for (const [groupName, rule] of Object.entries(guidelineRuleFormValues)) {
        if (
          // rule 값이 없는 경우
          !rule ||
          // 필수 장면이 아니고, value 값이 없는 경우
          (!isInArray(groupName, BEAUTY_OUTPUT_GROUP_NAMES) &&
            !isInArray(groupName, BEAUTY_ATTACHMENT_GROUP_NAMES) &&
            !Array.isArray(rule) &&
            !rule?.value) ||
          // 패션인 경우
          (!brand.isCategoryBeauty &&
            // 산출물 유형이 사진인데 릴스 GuidelineRule인 경우
            currentOutputTypeFl === CAMPAIGN_DTO_ENUMS.OUTPUT_TYPE_FL.PHOTO &&
            isInArray(groupName, FASHION_VIDEO_OUTPUT_GROUP_NAMES))
        ) {
          continue;
        } else {
          if (isInArray(groupName, GUIDELINE_RULE_ARRAY_GROUPS)) {
            // Array group인 경우 order 추가
            (rule as GuidelineRuleFormField[]).forEach((item, index) => {

              // 이미지 순서 변경 기능으로, "GUIDE_IMAGE" order 중요하게됨
              const orderNo = groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.GUIDE_IMAGE ? item.order : index + 1;

              const parsedRule = {
                ...item,
                order: orderNo,
                groupName,
              } 

              if (isInArray(groupName, GUIDELINE_RULE_NOT_ALLOWED_UNDEFINED_VALUE_GROUP_NAMES)) {
                // value가 undefined를 허용하지 않는 Group은 추가하지 않음
                if (item?.value !== undefined) {
                  parsedGuidelineRules.push(parsedRule);
                }
              } else {
                if (item?.value) {
                  parsedGuidelineRules.push(parsedRule);
                }
              }
            });
          } else if (isInArray(groupName, GUIDELINE_RULE_TAG_GROUPS)) {
            // Tag group인 경우 array였던 value를 text형태로 변경
            const { value: tag } = rule as GuidelineRuleFormField;

            if (!tag) {
              continue;
            } else {
              const parsedTag = (tag as string[])
                .filter(Boolean)
                .map((tag) => `${groupName === 'HASH_TAGS' ? '#' : '@'}${tag}`)
                .join(' ');

              if (!parsedTag) {
                continue;
              }

              parsedGuidelineRules.push({
                ...rule,
                groupName,
                value: parsedTag,
              });
            }
          } else {
            // 그외는 그대로 추가
            parsedGuidelineRules.push({
              ...rule,
              groupName,
            });
          }
        }
      }

      return parsedGuidelineRules;
    }, [brand]);

    const getInitialCommissionData = useCallback(
      (
        brand: Pick<BrandDto, 'currency' | 'destinationCountry'>,
        loadedCampaign: undefined | Pick<CampaignDto, 'commissionAmount' | 'commissionIncreaseMaxStep'>,
      ) => {
        // 원고료 기본값 설정
        const commissionOptions = getCampaignCommissionOptions(brand);
        const settableCommissionOption =
          commissionOptions.find((option) => {
            return option.commission === loadedCampaign?.commissionAmount;
          }) ?? commissionOptions[1]; // [1] => 30만원

        return {
          commissionAmount: settableCommissionOption?.commission,
          commissionPrice: settableCommissionOption?.commissionPrice,
          commissionIncreaseMaxStep:
            loadedCampaign?.commissionIncreaseMaxStep == null
              ? settableCommissionOption?.maxStep ?? 0
              : Math.min(loadedCampaign?.commissionIncreaseMaxStep, settableCommissionOption?.maxStep ?? 0),
        };
      },
      [],
    );

    const showAlertModal = useCallback(
      ({ title, text, confirm, cancel, maxWidth }: { title?: ReactNode; text: ReactNode; confirm?: () => void; cancel?: () => void; maxWidth?: number }) => {
        showModal({
          hideCloseButton: true,
          maxWidth: maxWidth || 380,
          title,
          text,
          confirm,
          cancel,
        });
      },
      [],
    );

    useEffect(() => {
      /**
       * commissionAmount 변경 시 캠페인 정보 업데이트
       * TODO: 추후 store 걷어낸 후 아래 로직 삭제
       */
      setLegacyCampaign({
        ...legacyCampaign,
        commissionAmount,
      });
    }, [commissionAmount]);

    // Step 변경 시 scroll 초기화
    useEffect(() => {
      window.scrollTo({ top: 0 });
    }, [step]);

    return (
      <>
        <FormProvider {...campaignFormMethods}>
          <CampaignFormContext.Provider
            value={{
              modal: {
                showModal,
                showAlertModal,
                closeModal,
                showOverlays,
                closeOverlays,
              },
              auth,
              brand,
              refetchBrand,
              setLegacyBrand,
              settings,
              products,
              refetchProducts,
              campaignFormMethods,
              guidelineRuleFormMethods,
              campaignPaymentFormMethods,
              temporaryKey,
              setTemporaryKey,
              step,
              setStep,
              checkedInspectionAgreement,
              setCheckedInspectionAgreement,
              showLoader,
              setShowLoader,
              isInitializedGuidelineRules,
              setIsInitializedGuidelineRules,
              updateCampaignId,
              getGuidelineRulesParams,
              prepareStoreCampaignFormData,
              storeOrUpdateTemporaryCampaignMutate,
              initializeCampaignFormData,
              getParsedCampaign,
              getParsedGuidelineRules,
              getInitialCommissionData,
            }}
          >
            {children}
          </CampaignFormContext.Provider>
        </FormProvider>
      </>
    );
  },
);
